import { Flex, Text } from '@mantine/core';
import { Dropzone, FileRejection } from '@mantine/dropzone';
import { IconPaperclip } from '@tabler/icons-react';
import { ReactNode } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { CHAT_FILE_DROPZONE_ACCEPT } from '../chats/chatFileTypes';
import { SiestaMode } from '../types';

type Props = {
  onDrop: (files: File[]) => void;
  onReject: (rejections: FileRejection[]) => void;
  siestaMode: SiestaMode;
  children: ReactNode;
};

// Wraps the chat panel so files can be dragged anywhere onto it. Lays itself out as a flex column
// that fills the available height, so the wrapped message area and input keep their normal layout.
export function ChatFileDropzone({ onDrop, onReject, siestaMode, children }: Props) {
  // Attaching is only allowed in chat mode, where the target conversation is visible. In Steering or
  // Chats there is no visible conversation, so a drop could silently land on the wrong one.
  const disabled = siestaMode !== 'chat';

  return (
    <Dropzone
      onDrop={onDrop}
      onReject={onReject}
      accept={CHAT_FILE_DROPZONE_ACCEPT}
      activateOnClick={false}
      enablePointerEvents
      disabled={disabled}
      multiple
      flex={1}
      mih={0}
      p={0}
      pos="relative"
      style={{
        border: 'none',
        background: 'transparent',
        display: 'flex',
        flexDirection: 'column',
      }}
      styles={{ inner: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } }}
    >
      <Dropzone.Accept>
        <DropOverlay tone="accept" label="Drop files to attach" />
      </Dropzone.Accept>
      <Dropzone.Reject>
        <DropOverlay tone="reject" label="Unsupported file type" />
      </Dropzone.Reject>
      {children}
    </Dropzone>
  );
}

function DropOverlay({ tone, label }: { tone: 'accept' | 'reject'; label: string }) {
  const borderColor =
    tone === 'reject' ? 'var(--mantine-color-red-5)' : 'var(--mantine-color-gray-4)';

  return (
    <Flex
      pos="absolute"
      align="center"
      justify="center"
      gap="xs"
      c={tone === 'reject' ? 'red.4' : 'gray.1'}
      style={{
        inset: 0,
        zIndex: 3,
        borderRadius: 12,
        border: `2px dashed ${borderColor}`,
        background: 'rgba(0, 0, 0, 0.6)',
        pointerEvents: 'none',
      }}
    >
      <IconPaperclip size={16} />
      <Text size="sm" fw={FONT_WEIGHTS.medium}>
        {label}
      </Text>
    </Flex>
  );
}
